import "./instrument";

import { add, initAll, track } from "@amplitude/unified";
import * as Sentry from "@sentry/react";
import {
	ErrorComponent as RouterErrorComponent,
	createRouter,
} from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { ErrorPage } from "./components/auth/auth";
import { BackgroundContainer } from "./components/base";
import { routeTree } from "./routeTree.gen";
import { routerAtom } from "./store/router";
import {
	reportAppError,
	telemetryContextPlugin,
	watchTelemetryUser,
} from "./utils/telemetry";
import { initTolgee } from "./utils/tolgee";

import "handsontable/dist/handsontable.full.min.css";

initTolgee();

const amplitudeKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
if (!amplitudeKey) {
	console.error(
		"[amplitude] VITE_AMPLITUDE_API_KEY is not set — no events will be sent",
	);
} else {
	add(telemetryContextPlugin);
	initAll(amplitudeKey, {
		serverZone: "US",
		analytics: { autocapture: true },
		sessionReplay: { sampleRate: 1 },
	});
}

const stopTelemetryUser = watchTelemetryUser();
import.meta.hot?.dispose(stopTelemetryUser);

export const router = createRouter({
	routeTree: routeTree,
	defaultPendingComponent: () => <BackgroundContainer />,
	defaultErrorComponent: ErrorPage,
	defaultOnCatch: (error, errorInfo) => {
		reportAppError(error, "router", errorInfo.componentStack);
	},
	// defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
getDefaultStore().set(routerAtom, router); // HMR Hack

const rootElement = document.getElementById("app")!;
if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<React.StrictMode>
			<Sentry.ErrorBoundary
				fallback={({ error }) => <RouterErrorComponent error={error} />}
			>
				<App />
			</Sentry.ErrorBoundary>
		</React.StrictMode>,
	);
	if (amplitudeKey) {
		track("App Loaded");
	}
}
